import { useCallback, useEffect, useMemo, useState } from "react";
import { useActiveAccount } from "applesauce-react/hooks";
import { mapEventsToStore } from "applesauce-core";
import { getSeenRelays, type Filter } from "applesauce-core/helpers";
import { PublicContactsModel } from "applesauce-core/models";
import { onlyEvents, Relay, type RelayCountResponse } from "applesauce-relay";
import { combineLatest, firstValueFrom, of, type Observable } from "rxjs";
import { catchError, filter, map, take, timeout } from "rxjs/operators";
import { use$ } from "@/hooks/use$";
import { useEventStore } from "@/hooks/useEventStore";
import { resilientSubscription } from "@/lib/resilientSubscription";
import {
  COMMENT_KIND,
  COVER_NOTE_KIND,
  ISSUE_KIND,
  PATCH_KIND,
  PR_KIND,
  REPO_KIND,
  STATUS_CLOSED,
  STATUS_DRAFT,
  STATUS_OPEN,
  STATUS_RESOLVED,
} from "@/lib/nip34";
import { normalizeUrl } from "@/lib/url";
import { pool } from "@/services/nostr";

const FOLLOW_BATCH_SIZE = 100;
const GIT_ROOT_KIND_TAGS = [REPO_KIND, ISSUE_KIND, PATCH_KIND, PR_KIND].map(
  String,
);

export interface GraspServiceCounts {
  repositories: CountValue;
  issues: CountValue;
  pullRequests: CountValue;
}

export interface PersonalGraspCounts extends GraspServiceCounts {
  interactions: CountValue;
}

export interface FollowRepositoryPresence {
  people: number;
  repositoryAnnouncements: number;
  samplePubkeys: string[];
}

/** Undefined is loading; null means the relay did not provide a usable count. */
export type CountValue = number | null | undefined;

export type PrivateGraspAccessState =
  | "idle"
  | "checking"
  | "allowed"
  | "denied"
  | "unavailable";

/**
 * Explicit, user-triggered GRASP-08 membership check.
 *
 * Authentication reveals the account pubkey to the service, so this never
 * runs automatically. A successful NIP-42 AUTH proves whitelist membership
 * because GRASP-08 requires non-members to receive `restricted:`.
 */
export function usePrivateGraspAccessCheck(relayUrl: string | undefined): {
  state: PrivateGraspAccessState;
  check: () => Promise<void>;
  signedIn: boolean;
} {
  const account = useActiveAccount();
  const [state, setState] = useState<PrivateGraspAccessState>("idle");

  useEffect(() => setState("idle"), [relayUrl, account?.pubkey]);

  const check = useCallback(async () => {
    if (!relayUrl || !account) return;
    setState("checking");
    const relay = new Relay(relayUrl);
    const probe = relay
      .count({ kinds: [REPO_KIND] } as Filter)
      .subscribe({ error: () => undefined });

    try {
      await firstValueFrom(
        relay.challenge$.pipe(
          filter((challenge): challenge is string => !!challenge),
          take(1),
          timeout({ first: 8_000 }),
        ),
      );
      const response = await relay.authenticate(account.signer);
      if (response.ok) {
        setState("allowed");
      } else if (/restricted:/i.test(response.message ?? "")) {
        setState("denied");
      } else {
        setState("unavailable");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "";
      setState(/restricted:/i.test(message) ? "denied" : "unavailable");
    } finally {
      probe.unsubscribe();
      relay.close();
    }
  }, [account, relayUrl]);

  return { state, check, signedIn: !!account };
}

function countFilter(relayUrl: string, filter: Filter): Observable<CountValue> {
  return (
    pool.count([relayUrl], filter) as Observable<
      Record<string, RelayCountResponse>
    >
  ).pipe(
    map((responses) =>
      Object.values(responses).reduce(
        (total, response) => total + response.count,
        0,
      ),
    ),
    catchError(() => of(null)),
  );
}

function interactionCount(
  relayUrl: string,
  pubkey: string,
): Observable<CountValue> {
  return combineLatest([
    countFilter(relayUrl, {
      kinds: [COMMENT_KIND],
      authors: [pubkey],
      "#K": GIT_ROOT_KIND_TAGS,
    } as Filter),
    countFilter(relayUrl, {
      kinds: [7],
      authors: [pubkey],
      "#k": GIT_ROOT_KIND_TAGS,
    } as Filter),
    countFilter(relayUrl, {
      kinds: [
        COVER_NOTE_KIND,
        STATUS_OPEN,
        STATUS_RESOLVED,
        STATUS_CLOSED,
        STATUS_DRAFT,
      ],
      authors: [pubkey],
      "#k": GIT_ROOT_KIND_TAGS,
    } as Filter),
  ]).pipe(
    map((counts) =>
      counts.some((count) => count === null)
        ? null
        : counts.reduce<number>((sum, count) => sum + (count ?? 0), 0),
    ),
  );
}

/** NIP-45 totals for the whole service and, when signed in, the active account. */
export function useGraspServiceCounts(relayUrl: string | undefined): {
  service: GraspServiceCounts | undefined;
  personal: PersonalGraspCounts | undefined;
  signedIn: boolean;
} {
  const account = useActiveAccount();
  const pubkey = account?.pubkey;
  const service = use$(() => {
    if (!relayUrl) return undefined;
    return combineLatest({
      repositories: countFilter(relayUrl, {
        kinds: [REPO_KIND],
      } as Filter),
      issues: countFilter(relayUrl, { kinds: [ISSUE_KIND] } as Filter),
      pullRequests: countFilter(relayUrl, {
        kinds: [PATCH_KIND, PR_KIND],
      } as Filter),
    });
  }, [relayUrl]);
  const personal = use$(() => {
    if (!relayUrl || !pubkey) return undefined;
    return combineLatest({
      repositories: countFilter(relayUrl, {
        kinds: [REPO_KIND],
        authors: [pubkey],
      } as Filter),
      issues: countFilter(relayUrl, {
        kinds: [ISSUE_KIND],
        authors: [pubkey],
      } as Filter),
      pullRequests: countFilter(relayUrl, {
        kinds: [PATCH_KIND, PR_KIND],
        authors: [pubkey],
      } as Filter),
      interactions: interactionCount(relayUrl, pubkey),
    });
  }, [relayUrl, pubkey]);

  return { service, personal, signedIn: !!pubkey };
}

/** Repository announcements on this relay from people in the account's NIP-02 list. */
export function useFollowRepositoryPresence(
  relayUrl: string | undefined,
): FollowRepositoryPresence | undefined {
  const account = useActiveAccount();
  const pubkey = account?.pubkey;
  const store = useEventStore();
  const followPubkeys = use$(() => {
    if (!pubkey) return undefined;
    return store
      .model(PublicContactsModel, pubkey)
      .pipe(map((contacts) => contacts?.map((contact) => contact.pubkey)));
  }, [pubkey, store]);
  const followKey = useMemo(
    () => [...(followPubkeys ?? [])].sort().join(","),
    [followPubkeys],
  );

  use$(() => {
    if (!relayUrl || !followPubkeys?.length) return undefined;
    const filters: Filter[] = [];
    for (
      let index = 0;
      index < followPubkeys.length;
      index += FOLLOW_BATCH_SIZE
    ) {
      filters.push({
        kinds: [REPO_KIND],
        authors: followPubkeys.slice(index, index + FOLLOW_BATCH_SIZE),
      } as Filter);
    }
    return resilientSubscription(pool, [relayUrl], filters, {
      paginate: true,
    }).pipe(onlyEvents(), mapEventsToStore(store));
  }, [relayUrl, followKey, store]);

  return use$(() => {
    if (!pubkey || !relayUrl || followPubkeys === undefined) return undefined;
    if (followPubkeys.length === 0) {
      return of({ people: 0, repositoryAnnouncements: 0, samplePubkeys: [] });
    }
    const normalizedRelay = normalizeUrl(relayUrl);
    return store
      .timeline([{ kinds: [REPO_KIND], authors: followPubkeys } as Filter])
      .pipe(
        map((events) => {
          const onRelay = events.filter((event) =>
            [...(getSeenRelays(event) ?? [])]
              .map(normalizeUrl)
              .includes(normalizedRelay),
          );
          const authors = [...new Set(onRelay.map((event) => event.pubkey))];
          return {
            people: authors.length,
            repositoryAnnouncements: onRelay.length,
            samplePubkeys: authors.slice(0, 5),
          };
        }),
      );
  }, [pubkey, relayUrl, followKey, store]);
}
